import { isCalculatorGroupRoleV1 } from "../calculator-group-role-v1.js";

const SANKEY_FIELDS = Object.freeze([
    "groups",
    "items",
    "unquantifiedGroups",
]);
const UNQUANTIFIED_GROUP_FIELDS = Object.freeze([
    "rowId",
    "groupId",
    "role",
    "presence",
    "rate",
    "denormalizedRate",
    "rawWeight",
    "provenance",
    "traceIds",
]);
const UNQUANTIFIED_ITEM_FIELDS = Object.freeze([
    "rowId",
    "groupId",
    "itemId",
    "presence",
    "rate",
    "denormalizedRate",
    "rawWeight",
    "quantity",
    "expectedQuantity",
    "provenance",
    "traceIds",
]);
const LOOT_ROW_PROVENANCE_FIELDS = Object.freeze([
    "presence",
    "dropRate",
    "presenceTraceIds",
    "dropRateTraceIds",
]);
const GROUP_RATE_AUTHORING_FIELDS = Object.freeze([
    "groupId",
    "normalized",
    "denormalized",
    "rawWeight",
    "shadowedBonuses",
]);
const SHADOWED_GROUP_BONUS_FIELDS = Object.freeze([
    "kind",
    "rawWeight",
    "subject",
    "traceIds",
]);
const SHADOWED_GROUP_BONUS_KINDS = new Set([
    "equipment",
    "petSpecial",
    "petTalent",
    "petSkill",
    "mastery",
]);
const PRESENCE_STATES = new Set(["present", "absent", "unknown"]);

export function createCalculatorLootProjectionValidator(adapter) {
    const {
        fail,
        requireArray,
        requireExactFields,
        requireItemId,
        requireRecord,
        requireString,
        validateRate,
        validateExpectedItemQuantity,
        validateItemQuantity,
        validateStatTermSubject,
        validateTraceId,
    } = adapter;

    function validateRawWeight(value, path) {
        const rate = validateRate(value, path);
        if (rate.state === "known" && rate.basis !== "rawWeight") {
            fail(`${path}.basis`, "known raw weight must use rawWeight basis");
        }
    }

    function validateDenormalizedRate(value, path) {
        const rate = validateRate(value, path);
        if (rate.state === "known" && rate.basis !== "percent") {
            fail(`${path}.basis`, "known denormalized rate must use percent basis");
        }
    }

    function validateRateBasis(value, basis, path, label) {
        const rate = validateRate(value, path);
        if (rate.state === "known" && rate.basis !== basis) {
            fail(`${path}.basis`, `known ${label} must use ${basis} basis`);
        }
        return rate;
    }

    function validateQuantifiedRate(value, path) {
        const rate = requireExactFields(value, ["value", "basis"], path);
        return validateRateBasis(
            {
                state: "known",
                value: rate.value,
                basis: rate.basis,
            },
            "ratio",
            path,
            "quantified rate",
        );
    }

    function ratesEqual(left, right) {
        if (left.state !== right.state) {
            return false;
        }
        if (left.state === "known") {
            return left.value === right.value && left.basis === right.basis;
        }
        if (left.state === "unknown") {
            return true;
        }
        return left.reason.code === right.reason.code
            && left.reason.sourceId === right.reason.sourceId
            && left.reason.detailCode === right.reason.detailCode;
    }

    function validateAbsentDropRate(presence, rate, path) {
        if (
            presence === "absent"
            && (
                rate.state !== "known"
                || rate.value !== 0
                || rate.basis !== "ratio"
            )
        ) {
            fail(path, "an absent row must have a known zero ratio rate");
        }
    }

    function validateProjectionTraceIds(value, path) {
        const traceIds = new Set();
        requireArray(value, path).forEach((trace, index) => {
            const tracePath = `${path}[${index}]`;
            const traceId = validateTraceId(
                requireRecord(trace, tracePath).traceId,
                `${tracePath}.traceId`,
            );
            if (traceIds.has(traceId)) {
                fail(`${tracePath}.traceId`, "duplicate projection trace ID");
            }
            traceIds.add(traceId);
        });
        return traceIds;
    }

    function validateGroupRateAuthoring(value, path) {
        const groupIds = new Set();
        requireArray(value, path).forEach((entry, index) => {
            const entryPath = `${path}[${index}]`;
            const row = requireExactFields(
                entry,
                GROUP_RATE_AUTHORING_FIELDS,
                entryPath,
            );
            const groupId = requireString(row.groupId, `${entryPath}.groupId`);
            if (groupIds.has(groupId)) {
                fail(`${entryPath}.groupId`, "duplicate group authoring row");
            }
            groupIds.add(groupId);
            validateRateBasis(
                row.normalized,
                "percent",
                `${entryPath}.normalized`,
                "normalized authoring rate",
            );
            validateRateBasis(
                row.denormalized,
                "denormalizedWeight",
                `${entryPath}.denormalized`,
                "denormalized authoring rate",
            );
            validateRateBasis(
                row.rawWeight,
                "rawWeight",
                `${entryPath}.rawWeight`,
                "raw-weight authoring rate",
            );
            requireArray(row.shadowedBonuses, `${entryPath}.shadowedBonuses`)
                .forEach((bonus, bonusIndex) => {
                    const bonusPath = `${entryPath}.shadowedBonuses[${bonusIndex}]`;
                    const record = requireExactFields(
                        bonus,
                        SHADOWED_GROUP_BONUS_FIELDS,
                        bonusPath,
                    );
                    const kind = requireString(record.kind, `${bonusPath}.kind`);
                    if (!SHADOWED_GROUP_BONUS_KINDS.has(kind)) {
                        fail(`${bonusPath}.kind`, "unsupported shadowed group bonus kind");
                    }
                    validateRawWeight(record.rawWeight, `${bonusPath}.rawWeight`);
                    validateStatTermSubject(record.subject, `${bonusPath}.subject`);
                    validateTraceIds(record.traceIds, `${bonusPath}.traceIds`);
                });
        });
        return groupIds;
    }

    function validateTraceIds(value, path) {
        requireArray(value, path).forEach((traceId, index) => {
            validateTraceId(traceId, `${path}[${index}]`);
        });
    }

    function validateRowProvenance(
        value,
        traceIdsValue,
        projectionTraceIds,
        path,
        traceIdsPath,
    ) {
        const provenance = requireExactFields(
            value,
            LOOT_ROW_PROVENANCE_FIELDS,
            path,
        );
        if (!PRESENCE_STATES.has(provenance.presence)) {
            fail(`${path}.presence`, "unsupported presence state");
        }
        const dropRate = validateRateBasis(
            provenance.dropRate,
            "ratio",
            `${path}.dropRate`,
            "row drop rate",
        );
        validateTraceIds(provenance.presenceTraceIds, `${path}.presenceTraceIds`);
        validateTraceIds(provenance.dropRateTraceIds, `${path}.dropRateTraceIds`);
        validateTraceIds(traceIdsValue, traceIdsPath);
        const rowTraceIds = new Set(traceIdsValue);
        const dropRateTraceIds = new Set(provenance.dropRateTraceIds);
        provenance.presenceTraceIds.forEach((traceId, index) => {
            if (!dropRateTraceIds.has(traceId)) {
                fail(
                    `${path}.presenceTraceIds[${index}]`,
                    "presence dependency is missing from drop-rate dependencies",
                );
            }
        });
        [
            [provenance.presenceTraceIds, "presenceTraceIds"],
            [provenance.dropRateTraceIds, "dropRateTraceIds"],
        ].forEach(([traceIds, field]) => {
            traceIds.forEach((traceId, index) => {
                if (!rowTraceIds.has(traceId)) {
                    fail(
                        `${path}.${field}[${index}]`,
                        "focused dependency is missing from row traceIds",
                    );
                }
                if (!projectionTraceIds.has(traceId)) {
                    fail(
                        `${path}.${field}[${index}]`,
                        "focused dependency references an unknown projection trace",
                    );
                }
            });
        });
        return {
            presence: provenance.presence,
            dropRate,
        };
    }

    function validateUnquantifiedGroup(value, projectionTraceIds, path) {
        const row = requireExactFields(value, UNQUANTIFIED_GROUP_FIELDS, path);
        requireString(row.rowId, `${path}.rowId`);
        requireString(row.groupId, `${path}.groupId`);
        if (!isCalculatorGroupRoleV1(row.role)) {
            fail(`${path}.role`, "unsupported group role");
        }
        if (!PRESENCE_STATES.has(row.presence)) {
            fail(`${path}.presence`, "unsupported presence state");
        }
        const rate = validateRate(row.rate, `${path}.rate`);
        validateDenormalizedRate(
            row.denormalizedRate,
            `${path}.denormalizedRate`,
        );
        validateRawWeight(row.rawWeight, `${path}.rawWeight`);
        validateTraceIds(row.traceIds, `${path}.traceIds`);
        const provenance = validateRowProvenance(
            row.provenance,
            row.traceIds,
            projectionTraceIds,
            `${path}.provenance`,
            `${path}.traceIds`,
        );
        if (provenance.presence !== row.presence) {
            fail(`${path}.provenance.presence`, "must match the row presence");
        }
        if (!ratesEqual(provenance.dropRate, rate)) {
            fail(`${path}.provenance.dropRate`, "must match the row rate");
        }
        validateAbsentDropRate(row.presence, rate, `${path}.rate`);
    }

    function validateUnquantifiedItem(value, projectionTraceIds, path) {
        const row = requireExactFields(value, UNQUANTIFIED_ITEM_FIELDS, path);
        requireString(row.rowId, `${path}.rowId`);
        if (row.groupId !== null) {
            requireString(row.groupId, `${path}.groupId`);
        }
        requireItemId(row.itemId, `${path}.itemId`);
        if (!PRESENCE_STATES.has(row.presence)) {
            fail(`${path}.presence`, "unsupported presence state");
        }
        const rate = validateRate(row.rate, `${path}.rate`);
        validateDenormalizedRate(
            row.denormalizedRate,
            `${path}.denormalizedRate`,
        );
        validateRawWeight(row.rawWeight, `${path}.rawWeight`);
        validateItemQuantity(row.quantity, `${path}.quantity`);
        validateExpectedItemQuantity(
            row.expectedQuantity,
            `${path}.expectedQuantity`,
        );
        validateTraceIds(row.traceIds, `${path}.traceIds`);
        const provenance = validateRowProvenance(
            row.provenance,
            row.traceIds,
            projectionTraceIds,
            `${path}.provenance`,
            `${path}.traceIds`,
        );
        if (provenance.presence !== row.presence) {
            fail(`${path}.provenance.presence`, "must match the row presence");
        }
        if (!ratesEqual(provenance.dropRate, rate)) {
            fail(`${path}.provenance.dropRate`, "must match the row rate");
        }
        validateAbsentDropRate(row.presence, rate, `${path}.rate`);
    }

    return function validateCalculatorLootProjection(
        sankeyValue,
        unquantifiedItemsValue,
        groupRateAuthoringValue,
        provenanceTracesValue,
        projectionPath,
    ) {
        const sankeyPath = `${projectionPath}.sankey`;
        const sankey = requireExactFields(sankeyValue, SANKEY_FIELDS, sankeyPath);
        const projectionTraceIds = validateProjectionTraceIds(
            provenanceTracesValue,
            `${projectionPath}.provenanceTraces`,
        );
        const projectedGroupIds = new Set();
        const authoringEligibleGroupIds = new Set();
        requireArray(sankey.groups, `${sankeyPath}.groups`)
            .forEach((row, index) => {
                const path = `${sankeyPath}.groups[${index}]`;
                const record = requireRecord(row, path);
                const groupId = requireString(record.groupId, `${path}.groupId`);
                if (projectedGroupIds.has(groupId)) {
                    fail(`${path}.groupId`, "duplicate projected group row");
                }
                projectedGroupIds.add(groupId);
                authoringEligibleGroupIds.add(groupId);
                const rate = validateQuantifiedRate(record.rate, `${path}.rate`);
                validateDenormalizedRate(
                    record.denormalizedRate,
                    `${path}.denormalizedRate`,
                );
                validateRawWeight(record.rawWeight, `${path}.rawWeight`);
                const provenance = validateRowProvenance(
                    record.provenance,
                    record.traceIds,
                    projectionTraceIds,
                    `${path}.provenance`,
                    `${path}.traceIds`,
                );
                if (provenance.presence !== "present") {
                    fail(
                        `${path}.provenance.presence`,
                        "a quantified row must be present",
                    );
                }
                if (!ratesEqual(provenance.dropRate, rate)) {
                    fail(
                        `${path}.provenance.dropRate`,
                        "must match the quantified group rate",
                    );
                }
            });
        requireArray(sankey.items, `${sankeyPath}.items`)
            .forEach((row, index) => {
                const path = `${sankeyPath}.items[${index}]`;
                const record = requireRecord(row, path);
                const groupId = requireString(record.groupId, `${path}.groupId`);
                requireItemId(record.itemId, `${path}.itemId`);
                if (!projectedGroupIds.has(groupId)) {
                    fail(`${path}.groupId`, "does not reference a quantified group");
                }
                validateQuantifiedRate(record.rate, `${path}.rate`);
                validateDenormalizedRate(
                    record.denormalizedRate,
                    `${path}.denormalizedRate`,
                );
                validateRawWeight(record.rawWeight, `${path}.rawWeight`);
                validateItemQuantity(record.quantity, `${path}.quantity`);
                validateExpectedItemQuantity(
                    record.expectedQuantity,
                    `${path}.expectedQuantity`,
                );
                const provenance = validateRowProvenance(
                    record.provenance,
                    record.traceIds,
                    projectionTraceIds,
                    `${path}.provenance`,
                    `${path}.traceIds`,
                );
                if (provenance.presence !== "present") {
                    fail(
                        `${path}.provenance.presence`,
                        "a quantified row must be present",
                    );
                }
                if (provenance.dropRate.state !== "known") {
                    fail(
                        `${path}.provenance.dropRate`,
                        "a quantified row must have a known drop-rate terminal",
                    );
                }
            });
        requireArray(
            sankey.unquantifiedGroups,
            `${sankeyPath}.unquantifiedGroups`,
        ).forEach((row, index) => {
            const path = `${sankeyPath}.unquantifiedGroups[${index}]`;
            validateUnquantifiedGroup(row, projectionTraceIds, path);
            const groupId = row.groupId;
            if (projectedGroupIds.has(groupId)) {
                fail(`${path}.groupId`, "duplicate projected group row");
            }
            projectedGroupIds.add(groupId);
            if (row.presence !== "absent") {
                authoringEligibleGroupIds.add(groupId);
            }
        });
        requireArray(
            unquantifiedItemsValue,
            `${projectionPath}.unquantifiedItems`,
        ).forEach((row, index) => validateUnquantifiedItem(
            row,
            projectionTraceIds,
            `${projectionPath}.unquantifiedItems[${index}]`,
        ));
        const authoringGroupIds = validateGroupRateAuthoring(
            groupRateAuthoringValue,
            `${projectionPath}.groupRateAuthoring`,
        );
        for (const groupId of authoringEligibleGroupIds) {
            if (!authoringGroupIds.has(groupId)) {
                fail(
                    `${projectionPath}.groupRateAuthoring`,
                    `missing authoring row for projected group ${groupId}`,
                );
            }
        }
        requireArray(groupRateAuthoringValue, `${projectionPath}.groupRateAuthoring`)
            .forEach((row, index) => {
                if (!authoringEligibleGroupIds.has(row.groupId)) {
                    fail(
                        `${projectionPath}.groupRateAuthoring[${index}].groupId`,
                        "has no active projected group",
                    );
                }
            });
    };
}
